/**
 * The 5×5 cartela.
 *
 * **Win detection never reads from this component.** That already ran
 * server-side inside the transaction that drew the number — §0.6: "The server
 * is authoritative for everything in a game… The client renders; it never
 * decides." Nothing here can change whether a player wins.
 *
 * What a player sees as "marked" has two independent sources, chosen by
 * `mode`:
 *  - `auto` (default): derived, every render, from `calledNumbers` — the
 *    original behaviour, and still the only thing that matters to the server.
 *  - `manual`: derived from `manuallyMarked`, a set the player builds by
 *    tapping cells themselves. Tapping only *toggles a local set* — it is
 *    bookkeeping for the player's eyes, not an input to anything that decides
 *    a round. A cell can only be toggled once its number has actually been
 *    called, so manual mode cannot be used to "pre-mark" a number that never
 *    comes up.
 */

import { memo } from 'react';
import type { CardLayout } from '../api/types.ts';

/** Row-major index of the free centre square. */
const FREE_CELL_INDEX = 12;

const COLUMN_LETTERS = ['B', 'I', 'N', 'G', 'O'] as const;

interface CartelaProps {
  layout: CardLayout;
  /** Every number drawn so far, from the server's log. */
  calledNumbers: ReadonlySet<number>;
  /** The most recent draw, highlighted so the eye can find it. */
  lastNumber: number | null;
  /** Dims the card once the round is over. */
  dimmed?: boolean;
  /** `auto` marks every called number automatically; `manual` waits for a tap. */
  mode?: 'auto' | 'manual';
  /** In `manual` mode, the numbers the player has tapped so far. */
  manuallyMarked?: ReadonlySet<number> | undefined;
  /** Fires only for a cell whose number has already been called. */
  onToggleMark?: (value: number) => void;
  /** Cell indices (0-24) to ring as the specific combo that won — outcome screen only. */
  winningCells?: ReadonlySet<number>;
}

export const Cartela = memo(function Cartela({
  layout,
  calledNumbers,
  lastNumber,
  dimmed = false,
  mode = 'auto',
  manuallyMarked,
  onToggleMark,
  winningCells,
}: CartelaProps) {
  return (
    <div className={`cartela${dimmed ? ' cartela--dimmed' : ''}`}>
      <div className="cartela__header" aria-hidden="true">
        {COLUMN_LETTERS.map((letter) => (
          <span key={letter} className="cartela__letter">
            {letter}
          </span>
        ))}
      </div>

      <div className="cartela__grid" role="grid" aria-label="Bingo card">
        {layout.map((value, index) => {
          const isFree = value === null || index === FREE_CELL_INDEX;
          const wasCalled = value !== null && calledNumbers.has(value);
          const isMarked =
            isFree || (mode === 'auto' ? wasCalled : (manuallyMarked?.has(value!) ?? false));
          const isLast = value !== null && value === lastNumber;
          const canToggle = mode === 'manual' && !isFree && wasCalled;
          const isWinningCell = winningCells?.has(index) ?? false;

          return (
            <div
              key={index}
              role="gridcell"
              aria-label={isFree ? 'free' : `${value}${isMarked ? ', marked' : ''}`}
              tabIndex={canToggle ? 0 : undefined}
              onClick={canToggle ? () => onToggleMark?.(value!) : undefined}
              onKeyDown={
                canToggle
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onToggleMark?.(value!);
                      }
                    }
                  : undefined
              }
              className={[
                'cell',
                isMarked ? 'cell--marked' : '',
                isFree ? 'cell--free' : '',
                isLast ? 'cell--just-called' : '',
                canToggle ? 'cell--tappable' : '',
                isWinningCell ? 'cell--winning' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {isFree ? <span className="cell__star">★</span> : value}
            </div>
          );
        })}
      </div>
    </div>
  );
});
